/**
 * `Н49`: номер счётчика **действующей** установки станции.
 *
 * Прежде карточка читала `station.meter_number`, но этого поля нет с 17.10.2025:
 * номер живёт в установках (`Meters`), и у станции их бывает несколько — снятые и
 * действующая. «Какой счётчик на станции» — это номер **действующей** установки, а
 * не первый попавшийся.
 *
 * Сервер отдаёт установки двумя видами, и оба встречаются в карточке:
 * - массив `Meters: [{ meter_number, is_active }]` — из `GET /plantData/:id`;
 * - плоский ключ `"Meters.meter_number"` — из `GET /stations/:id`.
 *
 * Поэтому разбор один на оба вида: помощник, а не два места с похожей логикой.
 */

/** Явно действующая установка: сервер отдаёт признак булевым или числом. */
function isActive(meter: any): boolean {
  const active = meter?.is_active ?? meter?.isActive;

  return active === true || active === 1 || active === "1";
}

function numberText(value: unknown): string | null {
  if (typeof value === "string" && value.trim() !== "") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);

  return null;
}

/**
 * Номер счётчика действующей установки; `null` — действующей установки нет
 * (номер неизвестен, а не «пустой»: карточка скажет «не установлен»).
 */
export function activeMeterNumber(station: any): string | null {
  if (!station) return null;

  // Плоский вид: ключ приходит уже выбранным действующим счётчиком.
  const flat = numberText(station["Meters.meter_number"]);
  if (flat) return flat;

  const meters = Array.isArray(station.Meters)
    ? station.Meters
    : station.Meters
      ? [station.Meters]
      : [];

  const active = meters.find((meter: any) => meter && isActive(meter));

  return numberText(active?.meter_number ?? active?.number);
}

/**
 * `(8)`: после принятой правки номера — записать его поверх станции в сторе в
 * том виде, в каком станция пришла (плоский ключ или `Meters[]`), чтобы окно
 * и список показали новый номер, даже если перечитывание не удалось.
 */
export function setActiveMeterNumber(station: any, number: string): void {
  if (!station) return;

  if ("Meters.meter_number" in station) {
    station["Meters.meter_number"] = number;
  }

  const meters = Array.isArray(station.Meters)
    ? station.Meters
    : station.Meters
      ? [station.Meters]
      : [];
  const active = meters.find((meter: any) => meter && isActive(meter));

  if (active) {
    active.meter_number = number;
  } else if (!("Meters.meter_number" in station)) {
    station["Meters.meter_number"] = number;
  }
}

/** То, что видит человек в карточке: номер или прямое «не установлен». */
export function meterNumberText(station: any): string {
  return activeMeterNumber(station) ?? "не установлен";
}
